import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSwipe } from '../utils/useSwipe';

const API = 'https://whitcomb-dashboard-api-production.up.railway.app';
const H = { 'X-API-Key': process.env.REACT_APP_API_KEY, 'Content-Type': 'application/json' };
const TODAY = new Date().toISOString().split('T')[0];

const CATEGORIES = [
  { value: 'Close',     label: 'Close',     cadence: 30,  color: '#1565c0' },
  { value: 'Important', label: 'Important', cadence: 90,  color: '#6a1b9a' },
  { value: 'Extended',  label: 'Extended',  cadence: 180, color: '#555' },
];

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date(TODAY) - new Date(dateStr)) / 86400000);
}

function urgencyColor(days, cadence) {
  if (days === null) return '#e57373';
  const ratio = days / cadence;
  if (ratio >= 1) return '#e57373';
  if (ratio >= 0.75) return '#ffb74d';
  return '#81c784';
}

function cadenceDays(category) {
  return CATEGORIES.find(c => c.value === category)?.cadence || 30;
}

const LOG_TYPES = [
  { value: 'date_night',   label: '🌙 Date Night' },
  { value: 'miles_1on1',   label: '🏈 1-on-1 with Miles' },
  { value: 'ellie_1on1',   label: '🌸 1-on-1 with Ellie' },
  { value: 'quality_time', label: '❤️ Quality Time (Jenny)' },
];

function TabPills({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 28 }}>
      {tabs.map((t, i) => (
        <button key={i} onClick={() => onChange(i)} style={{
          padding: '6px 16px',
          borderRadius: 99,
          border: 'none',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          background: active === i ? '#172A3A' : '#F0F0F0',
          color: active === i ? 'white' : '#666',
          transition: 'all 0.15s',
        }}>{t}</button>
      ))}
    </div>
  );
}

function FamilySection({ logs, onAddLog }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: TODAY, type: 'date_night', notes: '' });

  const thisMonth = TODAY.slice(0, 7);
  const thisWeek = (() => {
    const d = new Date();
    const mon = new Date(d);
    mon.setDate(d.getDate() - d.getDay() + 1);
    return mon.toISOString().split('T')[0];
  })();

  const dateNights      = logs.filter(l => l.type === 'date_night'   && l.date.startsWith(thisMonth));
  const milesTime       = logs.filter(l => l.type === 'miles_1on1'   && l.date.startsWith(thisMonth));
  const ellieTime       = logs.filter(l => l.type === 'ellie_1on1'   && l.date.startsWith(thisMonth));
  const qualityThisWeek = logs.filter(l => l.type === 'quality_time' && l.date >= thisWeek);

  const handleSubmit = async () => {
    if (!form.type || !form.date) return;
    const res = await fetch(`${API}/api/connect/logs`, { method: 'POST', headers: H, body: JSON.stringify({ ...form, people: form.type }) });
    const json = await res.json();
    onAddLog({ ...form, id: json.id, people: form.type });
    setForm({ date: TODAY, type: 'date_night', notes: '' });
    setShowForm(false);
  };

  const StatCard = ({ label, count, target, color }) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: 32, fontWeight: 700, color }}>{count}</span>
        {target  && <span style={{ fontSize: 14, color: '#999' }}>/ {target} this month</span>}
        {!target && <span style={{ fontSize: 14, color: '#999' }}>this week</span>}
      </div>
      {target && (
        <div style={{ marginTop: 8, height: 4, background: '#eee', borderRadius: 2 }}>
          <div style={{ height: 4, borderRadius: 2, background: color, width: `${Math.min(100, (count / target) * 100)}%`, transition: 'width 0.4s' }} />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatCard label="🌙 Date Nights"  count={dateNights.length}      target={2}    color="#c2185b" />
        <StatCard label="🏈 Miles 1-on-1" count={milesTime.length}       target={4}    color="#1565c0" />
        <StatCard label="🌸 Ellie 1-on-1" count={ellieTime.length}       target={4}    color="#6a1b9a" />
        <StatCard label="❤️ Quality Time" count={qualityThisWeek.length} target={4} color="#2e7d32" />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600, color: '#333' }}>Recent Log</div>
        <button onClick={() => setShowForm(f => !f)} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
          + Log Moment
        </button>
      </div>

      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#666' }}>Type</label>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 14 }}>
              {LOG_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#666' }}>Date</label>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 14 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <label style={{ fontSize: 12, color: '#666' }}>Notes (optional)</label>
            <input type="text" placeholder="What did you do?" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 14, width: '100%' }} />
          </div>
          <button onClick={handleSubmit} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 14, cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowForm(false)} style={{ background: '#eee', color: '#555', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer' }}>Cancel</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {logs.slice(0, 10).map(l => {
          const t = LOG_TYPES.find(x => x.value === l.type);
          return (
            <div key={l.id} style={{ background: '#fff', borderRadius: 10, padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 18 }}>{t?.label.split(' ')[0] || '📌'}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{t?.label.replace(/^\S+\s/, '') || l.type}</div>
                  {l.notes && <div style={{ fontSize: 12, color: '#888' }}>{l.notes}</div>}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#aaa' }}>{l.date}</div>
            </div>
          );
        })}
        {logs.length === 0 && <div style={{ color: '#aaa', fontSize: 14, padding: 12 }}>No moments logged yet. Start by logging a date night or 1-on-1.</div>}
      </div>
    </div>
  );
}

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === ',' && !inQ) { vals.push(cur); cur = ''; }
      else cur += line[i];
    }
    vals.push(cur);
    const obj = {};
    headers.forEach((h, i) => obj[h] = (vals[i] || '').trim());
    const notes = [obj.Organization, obj.Phone1, obj.Email1, obj.Email2, obj.Address, obj.Birthday].filter(Boolean).join(' | ');
    return obj.Name ? { name: obj.Name, category: 'Important', last_contact: TODAY, notes } : null;
  }).filter(Boolean);
}

function OthersSection({ people, onUpdate, onAdd, onDelete }) {
  const [showForm, setShowForm]     = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm]             = useState({ name: '', category: 'Important', last_contact: TODAY, notes: '' });
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting]   = useState(false);
  const fileRef = useRef();

  const overdue = people.filter(p => { const d = daysSince(p.last_contact); return d === null || d >= cadenceDays(p.relationship); })
    .sort((a, b) => (daysSince(b.last_contact) ?? 9999) - (daysSince(a.last_contact) ?? 9999));
  const current = people.filter(p => { const d = daysSince(p.last_contact); return d !== null && d < cadenceDays(p.relationship); })
    .sort((a, b) => (daysSince(b.last_contact) ?? 0) - (daysSince(a.last_contact) ?? 0));

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    const cadence = cadenceDays(form.category);
    await onAdd({ name: form.name, relationship: form.category, cadence_days: cadence, last_contact: form.last_contact, notes: form.notes });
    setForm({ name: '', category: 'Important', last_contact: TODAY, notes: '' });
    setShowForm(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCSV(ev.target.result);
      setImportRows(rows);
      setShowImport(true);
    };
    reader.readAsText(file);
  };

  const handleImportRowChange = (i, field, value) => {
    setImportRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  };

  const handleRemoveImportRow = (i) => {
    setImportRows(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleImportSubmit = async () => {
    setImporting(true);
    for (const row of importRows) {
      const cadence = cadenceDays(row.category);
      await onAdd({ name: row.name, relationship: row.category, cadence_days: cadence, last_contact: row.last_contact, notes: row.notes });
    }
    setImportRows([]);
    setShowImport(false);
    setImporting(false);
  };

  const PersonCard = ({ p }) => {
    const [editing, setEditing] = useState(false);
    const [editNotes, setEditNotes] = useState(p.notes || '');
    const days = daysSince(p.last_contact);
    const color = urgencyColor(days, cadenceDays(p.relationship));
    const cat = CATEGORIES.find(c => c.value === p.relationship);

    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</span>
              {cat && <span style={{ fontSize: 11, fontWeight: 600, color: cat.color, background: cat.color + '18', borderRadius: 10, padding: '2px 8px' }}>{cat.label}</span>}
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {cat ? `Every ${cat.cadence} days` : `Every ${p.cadence_days} days`}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <div style={{ background: color, color: '#fff', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
              {days === null ? 'Never contacted' : days === 0 ? 'Today' : `${days}d ago`}
            </div>
            <button onClick={() => onUpdate(p.id, { last_contact: TODAY })} style={{ background: '#f0f7f0', color: '#2e7d32', border: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>✓ Log contact</button>
          </div>
        </div>
        {p.notes && !editing && <div style={{ fontSize: 13, color: '#666', marginTop: 8 }}>{p.notes}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button onClick={() => setEditing(e => !e)} style={{ background: '#f5f5f5', border: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#555' }}>
            {editing ? 'Cancel' : '✏️ Notes'}
          </button>
          <button onClick={() => onDelete(p.id)} style={{ background: '#fff0f0', border: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#c62828' }}>Remove</button>
        </div>
        {editing && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <input value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Add notes..." style={{ flex: 1, padding: '6px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 13 }} />
            <button onClick={() => { onUpdate(p.id, { notes: editNotes }); setEditing(false); }} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>Save</button>
          </div>
        )}
      </div>
    );
  };

  if (showImport) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Review {importRows.length} contacts</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowImport(false)} style={{ background: '#eee', color: '#555', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleImportSubmit} disabled={importing} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
              {importing ? 'Importing...' : `Import ${importRows.length} contacts`}
            </button>
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Remove anyone you don't want to track. Set their category to set the contact cadence.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {importRows.map((row, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, flex: 1, minWidth: 140 }}>{row.name}</span>
              <select value={row.category} onChange={e => handleImportRowChange(i, 'category', e.target.value)} style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 13 }}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <input type="date" value={row.last_contact} onChange={e => handleImportRowChange(i, 'last_contact', e.target.value)} style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 13 }} />
              <button onClick={() => handleRemoveImportRow(i)} style={{ background: '#fff0f0', color: '#c62828', border: 'none', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>✕</button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#888' }}>{overdue.length} overdue · {current.length} current</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
          <button onClick={() => fileRef.current.click()} style={{ background: '#eee', color: '#555', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>📥 Import CSV</button>
          <button onClick={() => setShowForm(f => !f)} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>+ Add Person</button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#666' }}>Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 14, width: 160 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#666' }}>Category</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 14 }}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label} (every {c.cadence}d)</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#666' }}>Last Contact</label>
            <input type="date" value={form.last_contact} onChange={e => setForm(f => ({ ...f, last_contact: e.target.value }))} style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #ddd', fontSize: 14 }} />
          </div>
          <button onClick={handleAdd} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 14, cursor: 'pointer' }}>Add</button>
          <button onClick={() => setShowForm(false)} style={{ background: '#eee', color: '#555', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer' }}>Cancel</button>
        </div>
      )}

      {overdue.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#c62828', marginBottom: 8 }}>⚠️ Overdue</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {overdue.map(p => <PersonCard key={p.id} p={p} />)}
          </div>
        </>
      )}
      {current.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#2e7d32', marginBottom: 8 }}>✓ Current</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {current.map(p => <PersonCard key={p.id} p={p} />)}
          </div>
        </>
      )}
      {people.length === 0 && <div style={{ color: '#aaa', fontSize: 14, padding: 12 }}>No contacts yet. Add someone or import from your iPhone contacts (vCard).</div>}
    </div>
  );
}

export default function Connect() {
  const [activeSection, setActiveSection] = useState(0);
  const [slideDir, setSlideDir] = useState(null);
  const TABS_COUNT = 2;
  const goLeft  = useCallback(() => { setSlideDir('left');  setActiveSection(i => Math.max(0, i - 1)); }, []);
  const goRight = useCallback(() => { setSlideDir('right'); setActiveSection(i => Math.min(TABS_COUNT - 1, i + 1)); }, []);
  useSwipe(goRight, goLeft);
  const [people, setPeople]   = useState([]);
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/connect/people`, { headers: H }).then(r => r.json()),
      fetch(`${API}/api/connect/logs`,   { headers: H }).then(r => r.json()),
    ]).then(([p, l]) => {
      setPeople(Array.isArray(p) ? p : []);
      setLogs(Array.isArray(l) ? l : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleAddPerson = async (data) => {
    const res = await fetch(`${API}/api/connect/people`, { method: 'POST', headers: H, body: JSON.stringify(data) });
    const json = await res.json();
    setPeople(prev => [...prev, { ...data, id: json.id }]);
  };

  const handleUpdatePerson = async (id, fields) => {
    await fetch(`${API}/api/connect/people/${id}`, { method: 'PATCH', headers: H, body: JSON.stringify(fields) });
    setPeople(prev => prev.map(p => p.id === id ? { ...p, ...fields } : p));
  };

  const handleDeletePerson = async (id) => {
    if (!window.confirm('Remove this person?')) return;
    await fetch(`${API}/api/connect/people/${id}`, { method: 'DELETE', headers: H });
    setPeople(prev => prev.filter(p => p.id !== id));
  };

  const handleAddLog = (log) => setLogs(prev => [log, ...prev]);
  const familyLogs = logs.filter(l => ['date_night','miles_1on1','ellie_1on1','quality_time'].includes(l.type));

  if (loading) return <div style={{ padding: 40, color: '#aaa' }}>Loading...</div>;


  const slideStyle = slideDir ? {
    animation: `slideIn${slideDir === 'right' ? 'Left' : 'Right'} 0.25s ease forwards`,
  } : {};

  return (
    <div>
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(-40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <TabPills tabs={['👨‍👩‍👧 Family', '🤝 Others']} active={activeSection} onChange={(i) => { setSlideDir(i > activeSection ? 'right' : 'left'); setActiveSection(i); }} />
      <div key={activeSection} style={slideStyle}>
        {activeSection === 0 && <FamilySection logs={familyLogs} onAddLog={handleAddLog} />}
        {activeSection === 1 && <OthersSection people={people} onUpdate={handleUpdatePerson} onAdd={handleAddPerson} onDelete={handleDeletePerson} />}
      </div>
    </div>
  );
}
